import axios from 'axios';
import { getUser } from './userDataService';
import { jwtDecode } from 'jwt-decode';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Helper function to get current user ID
const getCurrentUserId = async () => {
  try {
    // First try to get from token (most reliable)
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        // Check if token has user ID directly
        if (decoded.userId) {
          return decoded.userId;
        }
        if (decoded.id) {
          return decoded.id;
        }
        // If token has email, we'll need to get user ID from API
        if (decoded.emails && decoded.emails.length > 0) {
          // Fall through to getUser() method
        }
      } catch (tokenError) {
        console.warn("Could not decode token, falling back to getUser():", tokenError);
      }
    }
    
    // Fallback: get from user API
    const userData = await getUser();
    // Check multiple possible response structures
    // Structure from getUser: {error: false, message: 'Ok', code: 200, result: {user: {...}}}
    if (userData?.result?.user?.id) {
      return userData.result.user.id;
    }
    // If result is the user object directly
    if (userData?.result?.id) {
      return userData.result.id;
    }
    // Fallback structures
    if (userData?.user?.id) {
      return userData.user.id;
    }
    if (userData?.id) {
      return userData.id;
    }
    console.error("User data structure:", userData);
    console.error("Available paths:", {
      'result.user.id': userData?.result?.user?.id,
      'result.id': userData?.result?.id,
      'user.id': userData?.user?.id,
      'id': userData?.id,
      'result keys': userData?.result ? Object.keys(userData.result) : 'no result'
    });
    throw new Error("Unable to get user ID - user data structure unexpected");
  } catch (error) {
    console.error("Error getting user ID:", error);
    throw error;
  }
};

export const getTransferableSkills = async () => {
  const token = localStorage.getItem('token');
 //console.log(token);
  try {
    const response = await axios.get(`${API_BASE_URL}/transferable-skill`, {
      headers: {
        'Authorization': `${token}`,
      },
    });

    // Check if 'result' is present in the response
    if (response.data.result) {
      // Filter out disabled skills or those not to be included in CV
      const filteredSkills = response.data.result.filter(
        skill => skill.isEnable && skill.isIncludeInCv
      );
      return filteredSkills;
    } else {
      console.error("No transferable skills data found.");
      return null;
    }
  } catch (error) {
    console.error("Error fetching transferable skills data:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || "Error fetching transferable skills data");
  }
};

// Get all transferable skills (including disabled ones) for editing
// This loads ALL skills from the database regardless of where they were added
// (control center or webapp) - ensures no existing skills are lost
export const getAllTransferableSkills = async () => {
  const token = localStorage.getItem('token');
  try {
    const response = await axios.get(`${API_BASE_URL}/transferable-skill`, {
      headers: {
        'Authorization': `${token}`,
      },
    });

    if (response.data.result) {
      // Check if result is the full object with transferableSkills array, or just the array
      if (response.data.result.transferableSkills && Array.isArray(response.data.result.transferableSkills)) {
        // It's the full object - return ALL skills from the array (regardless of source)
        // This includes skills added by mentors in control center AND skills added by users in webapp
        return response.data.result.transferableSkills;
      } else if (Array.isArray(response.data.result)) {
        // It's just the array (old format) - return all skills
        return response.data.result;
      } else {
        // Unexpected structure - log but return empty array to prevent errors
        console.warn("Unexpected transferable skills response structure:", response.data.result);
        return [];
      }
    } else {
      // No result - return empty array (no skills exist yet)
      return [];
    }
  } catch (error) {
    // If 404, no transferable skills document exists yet - return empty array
    if (error.response?.status === 404 || error.response?.status === 400) {
      console.log("No transferable skills document found - returning empty array");
      return [];
    }
    // For other errors, log and throw
    console.error("Error fetching transferable skills data:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || "Error fetching transferable skills data");
  }
};

// Check if transferable skills section is active for the user
// Returns true if section exists and is active, false otherwise
export const isTransferableSkillsActive = async () => {
  const token = localStorage.getItem('token');
  try {
    const response = await axios.get(`${API_BASE_URL}/transferable-skill`, {
      headers: {
        'Authorization': `${token}`,
      },
    });

    if (response.data.result) {
      // Check if result is the full object with isActive, or just the array
      if (response.data.result.isActive !== undefined) {
        // It's the full object - return isActive value (must be explicitly true)
        return response.data.result.isActive === true;
      } else if (Array.isArray(response.data.result)) {
        // It's just the array (old format) - assume active if section exists
        return true;
      }
    }
    // Default to false if we can't determine (safer to hide than show incorrectly)
    return false;
  } catch (error) {
    // If 404, section doesn't exist (isActive = false)
    if (error.response?.status === 404 || error.response?.status === 400) {
      return false;
    }
    // For other errors, default to false (safer to hide than show incorrectly)
    console.warn("Error checking transferable skills active status:", error.response?.data || error.message);
    return false;
  }
};

export const addTransferableSkill = async (skillData) => {
  const token = localStorage.getItem('token');
  try {
    const userId = await getCurrentUserId();
    
    const response = await axios.put(`${API_BASE_URL}/transferable-skill/add`, {
      userId: userId,
      name: skillData.name,
      comment: skillData.comment || '',
      point: Number(skillData.point) || 0,
      isIncludeInCv: skillData.isIncludeInCv !== undefined ? skillData.isIncludeInCv : true,
      isEnable: skillData.isEnable !== undefined ? skillData.isEnable : true,
    }, {
      headers: {
        'Authorization': `${token}`,
      },
    });

    if (response.data.result) {
      return response.data.result;
    } else {
      throw new Error("Failed to add transferable skill");
    }
  } catch (error) {
    console.error("Error adding transferable skill:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || "Error adding transferable skill");
  }
};

export const updateTransferableSkill = async (skillData) => {
  const token = localStorage.getItem('token');
  try {
    const userId = await getCurrentUserId();
    
    const response = await axios.put(`${API_BASE_URL}/transferable-skill`, {
      userId: userId,
      id: skillData.id,
      name: skillData.name,
      comment: skillData.comment,
      point: Number(skillData.point) || 0,
      isIncludeInCv: skillData.isIncludeInCv,
      isEnable: skillData.isEnable,
    }, {
      headers: {
        'Authorization': `${token}`,
      },
    });

    if (response.data.result) {
      return response.data.result;
    } else {
      throw new Error("Failed to update transferable skill");
    }
  } catch (error) {
    console.error("Error updating transferable skill:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || "Error updating transferable skill");
  }
};

export const getSkillCatalogue = async () => {
  const token = localStorage.getItem('token');
  try {
    const response = await axios.get(`${API_BASE_URL}/club/skill-catalogue`, {
      headers: { Authorization: `${token}` },
    });
    if (response.data?.result) {
      return response.data.result;
    }
    return { disabledDefaults: [], customSkills: [] };
  } catch (error) {
    console.error("Error fetching skill catalogue:", error.response?.data || error.message);
    return { disabledDefaults: [], customSkills: [] };
  }
};

export const deleteTransferableSkill = async (skillId) => {
  const token = localStorage.getItem('token');
  try {
    const userId = await getCurrentUserId();
    
    const response = await axios.delete(`${API_BASE_URL}/transferable-skill`, {
      headers: {
        'Authorization': `${token}`,
      },
      data: {
        userId: userId,
        id: skillId,
      },
    });

    if (response.data.result) {
      return response.data.result;
    } else {
      throw new Error("Failed to delete transferable skill");
    }
  } catch (error) {
    console.error("Error deleting transferable skill:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || "Error deleting transferable skill");
  }
};